import { useRef, useEffect, useState, useCallback } from "react";

// Royalty-free ambient study music URLs (using Web Audio API oscillators as fallback)
const AMBIENT_TRACKS = [
  // These would be replaced with actual royalty-free music files
  // For now, we generate ambient tones using Web Audio API
];

// Transition sound effects (short melodic stings)
const SOUND_EFFECTS = {
  sectionComplete: { frequency: 523.25, duration: 0.3, type: "sine" }, // C5
  lessonComplete: { frequency: 659.25, duration: 0.5, type: "triangle" }, // E5  
  quizCorrect: { frequency: 783.99, duration: 0.2, type: "sine" }, // G5
  quizWrong: { frequency: 293.66, duration: 0.3, type: "sawtooth" }, // D4
  notification: { frequency: 440, duration: 0.15, type: "sine" } // A4
};

// Audio Manager Hook
export function useAudioManager() {
  const audioContextRef = useRef(null);
  const ambientGainRef = useRef(null);
  const effectsGainRef = useRef(null);
  const ambientOscillatorRef = useRef(null);
  const [isAmbientPlaying, setIsAmbientPlaying] = useState(false);
  const [ambientVolume, setAmbientVolume] = useState(0.15);
  const [isMuted, setIsMuted] = useState(false);
  const [isInstructorSpeaking, setIsInstructorSpeaking] = useState(false);
  
  // Initialize Audio Context
  const initAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      
      // Create gain nodes for volume control
      ambientGainRef.current = audioContextRef.current.createGain();
      ambientGainRef.current.gain.value = ambientVolume;
      ambientGainRef.current.connect(audioContextRef.current.destination);
      
      effectsGainRef.current = audioContextRef.current.createGain();
      effectsGainRef.current.gain.value = 0.5;
      effectsGainRef.current.connect(audioContextRef.current.destination);
    }
    return audioContextRef.current;
  }, [ambientVolume]);
  
  // Generate ambient lo-fi beat using Web Audio API
  const startAmbientMusic = useCallback(() => {
    const ctx = initAudioContext();
    if (ctx.state === "suspended") {
      ctx.resume();
    }
    
    // Stop existing ambient if playing
    if (ambientOscillatorRef.current) {
      try {
        ambientOscillatorRef.current.stop();
      } catch (e) {}
    }
    
    // Create a simple ambient pad sound
    const osc = ctx.createOscillator();
    const filter = ctx.createBiquadFilter();
    
    osc.type = "sine";
    osc.frequency.value = 110; // Low A
    
    // Add subtle LFO for movement
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    lfo.frequency.value = 0.5; // Very slow
    lfoGain.gain.value = 5;
    lfo.connect(lfoGain);
    lfoGain.connect(osc.frequency);
    
    // Low-pass filter for warmth
    filter.type = "lowpass";
    filter.frequency.value = 400;
    filter.Q.value = 1;
    
    osc.connect(filter);
    filter.connect(ambientGainRef.current);
    
    osc.start();
    lfo.start();
    
    ambientOscillatorRef.current = osc;
    setIsAmbientPlaying(true);
    
    return () => {
      osc.stop();
      lfo.stop();
    };
  }, [initAudioContext]);
  
  // Stop ambient music
  const stopAmbientMusic = useCallback(() => {
    if (ambientOscillatorRef.current) {
      try {
        ambientOscillatorRef.current.stop();
      } catch (e) {}
      ambientOscillatorRef.current = null;
    }
    setIsAmbientPlaying(false);
  }, []);
  
  // Duck volume when instructor speaks
  const duckForSpeech = useCallback((speaking) => {
    setIsInstructorSpeaking(speaking);
    if (ambientGainRef.current) {
      const ctx = audioContextRef.current;
      if (ctx) {
        ambientGainRef.current.gain.cancelScheduledValues(ctx.currentTime);
        ambientGainRef.current.gain.setTargetAtTime(
          speaking ? 0 : ambientVolume,
          ctx.currentTime,
          0.1
        );
      }
    }
  }, [ambientVolume]);
  
  // Play sound effect
  const playEffect = useCallback((effectName) => {
    const effect = SOUND_EFFECTS[effectName];
    if (!effect) return;
    
    const ctx = initAudioContext();
    if (ctx.state === "suspended") {
      ctx.resume();
    }
    
    const osc = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    osc.type = effect.type;
    osc.frequency.value = effect.frequency;
    
    // Create envelope
    gainNode.gain.setValueAtTime(0, ctx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.05);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + effect.duration);
    
    osc.connect(gainNode);
    gainNode.connect(effectsGainRef.current);
    
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + effect.duration);
  }, [initAudioContext]);
  
  // Play completion sting (melodic sequence)
  const playCompletionSting = useCallback(() => {
    const ctx = initAudioContext();
    if (ctx.state === "suspended") {
      ctx.resume();
    }
    
    // Play a simple ascending arpeggio
    const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
    const duration = 0.15;
    
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      osc.type = "triangle";
      osc.frequency.value = freq;
      
      const startTime = ctx.currentTime + (i * duration);
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.2, startTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
      
      osc.connect(gainNode);
      gainNode.connect(effectsGainRef.current);
      
      osc.start(startTime);
      osc.stop(startTime + duration);
    });
  }, [initAudioContext]);
  
  // Set ambient volume
  const setVolume = useCallback((vol) => {
    setAmbientVolume(vol);
    if (ambientGainRef.current && !isInstructorSpeaking) {
      ambientGainRef.current.gain.value = vol;
    }
  }, [isInstructorSpeaking]);
  
  // Toggle mute
  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const newMuted = !prev;
      if (ambientGainRef.current) {
        ambientGainRef.current.gain.value = newMuted ? 0 : ambientVolume;
      }
      if (effectsGainRef.current) {
        effectsGainRef.current.gain.value = newMuted ? 0 : 0.5;
      }
      return newMuted;
    });
  }, [ambientVolume]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAmbientMusic();
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [stopAmbientMusic]);
  
  return {
    isAmbientPlaying,
    isMuted,
    ambientVolume,
    isInstructorSpeaking,
    startAmbientMusic,
    stopAmbientMusic,
    duckForSpeech,
    playEffect,
    playCompletionSting,
    setVolume,
    toggleMute
  };
}

// Audio Control Panel Component
export function AudioControlPanel({ 
  isVisible, 
  onClose,
  audioManager 
}) {
  if (!isVisible) return null;
  
  const {
    isAmbientPlaying,
    isMuted,
    ambientVolume,
    startAmbientMusic,
    stopAmbientMusic,
    setVolume,
    toggleMute,
    playCompletionSting
  } = audioManager;
  
  return (
    <div
      style={{
        position: "fixed",
        bottom: "170px",
        right: "20px",
        background: "#1a1a2a",
        border: "1px solid #333",
        borderRadius: "12px",
        padding: "15px",
        width: "220px",
        zIndex: 9998,
        boxShadow: "0 4px 15px rgba(0,0,0,0.3)"
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
        <h4 style={{ margin: 0, color: "#FFD700", fontSize: "0.9rem" }}>Audio Settings</h4>
        <button
          onClick={onClose}
          style={{
            background: "transparent",
            border: "none",
            color: "#666",
            cursor: "pointer",
            fontSize: "1rem"
          }}
        >
          ✕
        </button>
      </div>
      
      {/* Ambient Music Toggle */}
      <div style={{ marginBottom: "15px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <span style={{ color: "#aaa", fontSize: "0.85rem" }}>Study Ambiance</span>
          <button
            onClick={isAmbientPlaying ? stopAmbientMusic : startAmbientMusic}
            style={{
              background: isAmbientPlaying ? "#90EE90" : "#333",
              color: isAmbientPlaying ? "#000" : "#fff",
              border: "none",
              borderRadius: "15px",
              padding: "5px 12px",
              fontSize: "0.75rem",
              cursor: "pointer"
            }}
          >
            {isAmbientPlaying ? "ON" : "OFF"}
          </button>
        </div>
      </div>
      
      {/* Volume Slider */}
      <div style={{ marginBottom: "15px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "5px" }}>
          <span style={{ color: "#aaa", fontSize: "0.85rem" }}>Volume</span>
          <span style={{ color: "#FFD700", fontSize: "0.75rem" }}>{Math.round(ambientVolume * 100)}%</span>
        </div>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          value={ambientVolume}
          onChange={(e) => setVolume(parseFloat(e.target.value))}
          style={{
            width: "100%",
            accentColor: "#FFD700"
          }}
        />
      </div>
      
      {/* Mute Toggle */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
        <span style={{ color: "#aaa", fontSize: "0.85rem" }}>Mute All</span>
        <button
          onClick={toggleMute}
          style={{
            background: isMuted ? "#ff6b6b" : "#333",
            color: "#fff",
            border: "none",
            borderRadius: "15px",
            padding: "5px 12px",
            fontSize: "0.75rem",
            cursor: "pointer"
          }}
        >
          {isMuted ? "🔇 Muted" : "🔊 On"}
        </button>
      </div>
      
      {/* Test Sound */}
      <button
        onClick={playCompletionSting}
        style={{
          width: "100%",
          background: "#222",
          border: "1px solid #444",
          borderRadius: "8px",
          padding: "8px",
          color: "#aaa",
          fontSize: "0.8rem",
          cursor: "pointer"
        }}
      >
        🎵 Test Completion Sound
      </button>
    </div>
  );
}

export default useAudioManager;
